// Banestart: kort oplæg og målene, så man ved hvad der giver stjerner.

import { DIFFICULTIES, greenGoal, trustMax } from '../../sim';
import type { DifficultyId, LevelDef } from '../../sim';
import { h, setText } from '../dom';
import { ICON } from '../icons';

export type TeamRole = 'planner' | 'drift';

/** Holdet med Volt: spilleren vælger rolle, Volt tager den anden (spor "sandhed"). */
export interface TeamPick {
  role: TeamRole;
  /** Første bane lærer at trække en ledning: der er man Planlægger. */
  locked: boolean;
  pick(role: TeamRole): void;
}

const TEAM: Record<TeamRole, { name: string; job: string }> = {
  planner: { name: 'Planlægger', job: 'bygger og opgraderer ledninger og stationer' },
  drift: { name: 'Driftsvagt', job: 'holder frekvensen, forstærker, svarer på kortene og har nødknapperne' },
};

/** "Volt er Driftsvagt: forstærker, ..." for den rolle, spilleren ikke har. */
export function voltJob(role: TeamRole): string {
  const v = TEAM[role === 'planner' ? 'drift' : 'planner'];
  return `Volt er ${v.name.toLowerCase()} og ${v.job}.`;
}

function teamEl(team: TeamPick): HTMLElement {
  const note = h('p', { class: 'help team-note' }, voltJob(team.role));
  const btns: HTMLButtonElement[] = [];
  const row = h('div', { class: 'seg team-seg', role: 'radiogroup', 'aria-label': 'Din rolle' });
  for (const r of ['planner', 'drift'] as const) {
    const b = h(
      'button',
      {
        type: 'button',
        role: 'radio',
        class: 'seg-btn',
        'data-role': r,
        'aria-checked': String(r === team.role),
        disabled: team.locked && r !== team.role,
        onclick: () => {
          if (team.locked) return;
          for (const x of btns) x.setAttribute('aria-checked', String(x === b));
          setText(note, voltJob(r));
          team.pick(r);
        },
      },
      TEAM[r].name,
    ) as HTMLButtonElement;
    btns.push(b);
    row.append(b);
  }
  return h(
    'div',
    { class: 'team' },
    h('p', { class: 'team-head' }, h('b', {}, 'Du og Volt'), team.locked ? ' · første bane er du planlægger' : ' · vælg din rolle'),
    row,
    note,
  );
}

export function levelStartScreen(level: LevelDef, diff: DifficultyId, touch: boolean, handlers: { go(): void; back(): void }, team?: TeamPick): HTMLElement {
  const d = DIFFICULTIES[diff];
  // Co-op runde 4: "lobbyen lover 3", men med Volt har banen et hjerte mere (trustMax).
  const hearts = trustMax({ difficulty: diff, coop: !!team });
  const goal = greenGoal(level.greenGoalPct, diff);
  const how = touch ? 'Træk med fingeren fra en by til en mølle. Eller tryk på dem efter hinanden.' : 'Træk fra en by til en mølle. Eller klik på dem efter hinanden.';
  const tips = [
    h('p', { class: 'how' }, h('span', { class: 'how-icon', html: ICON.line }), how),
    // IGN-løkke runde 2: "briefingen forklarer ikke N-1". Kun de niveauer, hvor ledninger slår fra.
    d.n1 === 'required' || d.n1 === 'cascades'
      ? h(
          'p',
          { class: 'how' },
          h('span', { class: 'how-icon', html: ICON.station }),
          h(
            'span',
            {},
            h('b', {}, 'N-1'),
            ': en ledning over 100 % slår fra efter et par sekunder',
            d.n1 === 'cascades' ? ' og trækker naboerne med' : '',
            '. Giv hver by to veje ind, så den klarer sig, når én falder. Det giver også stjernen.',
          ),
        )
      : null,
    // Spor "sværhed": Orkan siger, hvad første år giver, og hvad der skal til for at mestre banen.
    d.firstYearCoach && d.coach === 'off'
      ? h(
          'p',
          { class: 'how' },
          h('span', { class: 'how-icon', html: ICON.target }),
          h(
            'span',
            {},
            h('b', {}, 'Første år'),
            ' viser en ring det vigtigste. Derefter er du alene. ',
            h('b', {}, 'Mesterkrav'),
            ': to veje ind til hver by, og intet mørkeur over 40\u00a0%.',
          ),
        )
      : null,
    // Playtest runde 3: Lynreserve og Nødaflastning blev aldrig forklaret, før træneren bad om dem.
    level.mechanics.includes('fcr')
      ? h(
          'p',
          { class: 'how' },
          h('span', { class: 'how-icon', html: ICON.bolt }),
          h('span', {}, h('b', {}, 'Lynreserve'), touch ? '' : ' (Q)', ' giver mørke byer 5 s ekstra. ', h('b', {}, 'Nødaflastning'), touch ? '' : ' (W)', ' slukker én by i 10 s, så de andre får strømmen.'),
        )
      : null,
    // Spor "indhold": banens ene nye mekanik står i briefen, før den kommer.
    level.mechanics.includes('norway')
      ? h(
          'p',
          { class: 'how new-mech' },
          h('span', { class: 'how-icon', html: ICON.fjeld }),
          h('span', {}, h('b', {}, 'Nyt: Ring til Norge'), touch ? '' : ' (E)', '. Når vinden sover, sender de norske vandkraftværker strøm gennem Skagerrak-kablet i 20 s.'),
        )
      : null,
    level.mechanics.includes('flywheel')
      ? h(
          'p',
          { class: 'how new-mech' },
          h('span', { class: 'how-icon', html: ICON.flywheel }),
          h('span', {}, h('b', {}, 'Nyt: Svinghjul'), '. Falder et værk ud, dykker frekvensen. Byg et svinghjul på samme side af Storebælt: det tager stødet, så byerne holder lyset.'),
        )
      : null,
  ].filter((x): x is HTMLParagraphElement => x !== null);
  // IGN-løkke 2, runde 2: Orkan-briefen på bane 6 rullede bag Start ved 1280x800. Med tre eller
  // flere tips står de i en kolonne for sig på en bred skærm.
  return h(
    'div',
    { class: tips.length >= 3 ? 'card levelstart many' : 'card levelstart' },
    h('p', { class: 'eyebrow' }, `Bane ${level.number} · ${level.year} · ${d.name}`),
    h('h1', { class: 'title small' }, level.title.toUpperCase()),
    h('p', { class: 'lead' }, level.intro),
    h(
      'ul',
      { class: 'goals' },
      h('li', {}, h('span', { class: 'goal-icon bolt', html: ICON.bolt }), h('span', {}, h('b', {}, 'Tænd byerne'), ' og hold dem tændt i ', String(level.years), ' år', level.finaleS ? ' plus en kort finale.' : '.')),
      h('li', {}, h('span', { class: 'goal-icon leaf', html: ICON.leaf }), h('span', {}, h('b', {}, `Mål: ${goal} % grøn strøm`), ' (to stjerner).')),
      h('li', {}, h('span', { class: 'goal-icon heart', html: ICON.heart }), h('span', {}, h('b', {}, `${hearts} ${hearts === 1 ? 'hjerte' : 'hjerter'}`), team ? ', ét af dem fra Volt' : '', '. En by, der går helt i sort, koster ét.', level.number <= (d.spareLevels ?? 0) ? ' Det sidste bliver på denne bane, når du har bygget.' : '')),
    ),
    h('div', { class: 'hows' }, ...tips),
    team ? teamEl(team) : null,
    h(
      'div',
      { class: 'actions' },
      h('button', { type: 'button', class: 'btn ghost', onclick: () => handlers.back() }, 'Tilbage'),
      h('button', { type: 'button', class: 'btn primary', autofocus: true, onclick: () => handlers.go() }, h('span', { html: ICON.play }), 'Start'),
    ),
  );
}
